import React, { useState, useEffect, useCallback } from 'react';
import { useAuthProvider } from '../../providers/AuthProvider';
import { useAlert } from '../../providers/AlertProvider';
import { getSessionUUID } from '../lib/AuthToken';

const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;
const EMPTY_ROW = Array(BOARD_WIDTH).fill(0);
const EMPTY_BOARD = Array(BOARD_HEIGHT).fill().map(() => [...EMPTY_ROW]);

// Tetris pieces (tetrominoes)
const TETROMINOES = {
    I: {
        shape: [
            [1, 1, 1, 1]
        ],
        color: '#00f5ff'
    },
    O: {
        shape: [
            [1, 1],
            [1, 1]
        ],
        color: '#ffed00'
    },
    T: {
        shape: [
            [0, 1, 0],
            [1, 1, 1]
        ],
        color: '#a000f0'
    },
    S: {
        shape: [
            [0, 1, 1],
            [1, 1, 0]
        ],
        color: '#00f000'
    },
    Z: {
        shape: [
            [1, 1, 0],
            [0, 1, 1]
        ],
        color: '#f00000'
    },
    J: {
        shape: [
            [1, 0, 0],
            [1, 1, 1]
        ],
        color: '#0000f5'
    },
    L: {
        shape: [
            [0, 0, 1],
            [1, 1, 1]
        ],
        color: '#ff8000'
    }
};

const TETROMINO_TYPES = Object.keys(TETROMINOES);

const createPiece = () => {
    const type = TETROMINO_TYPES[Math.floor(Math.random() * TETROMINO_TYPES.length)];
    return {
        type,
        shape: TETROMINOES[type].shape,
        color: TETROMINOES[type].color,
        x: Math.floor((BOARD_WIDTH - TETROMINOES[type].shape[0].length) / 2),
        y: 0
    };
};

const TetrisGame = ({ theme, onClose, onGameOver }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();
    
    const [board, setBoard] = useState(EMPTY_BOARD);
    const [currentPiece, setCurrentPiece] = useState(createPiece());
    const [nextPiece, setNextPiece] = useState(createPiece());
    const [score, setScore] = useState(0);
    const [level, setLevel] = useState(1);
    const [lines, setLines] = useState(0);
    const [gameRunning, setGameRunning] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [dropTime, setDropTime] = useState(1000);
    const [highScore, setHighScore] = useState(0);
    const [highScorePlayer, setHighScorePlayer] = useState('No one yet!');

    // Fetch current high score
    const fetchHighScore = async () => {
        try {
            const sessionToken = await getSessionUUID();
            const response = await fetch('/api/easter-egg/high-score', {
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setHighScore(data.high_score || 0);
                setHighScorePlayer(data.player_name || 'No one yet!');
            }
        } catch (error) {
            console.error('Error fetching high score:', error);
        }
    };

    // Submit new high score
    const submitHighScore = async (newScore) => {
        try {
            const sessionToken = await getSessionUUID();
            const response = await fetch('/api/easter-egg/high-score', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${sessionToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    score: newScore,
                    player_name: profile?.name || profile?.email || 'Anonymous',
                    user_id: profile?.id
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.new_record) {
                    showAlert({
                        message: '🎉 NEW HIGH SCORE! 🎉',
                        type: 'success',
                        buttons: [{ label: 'Awesome!', type: 'primary' }]
                    });
                    setHighScore(newScore);
                }
            }
        } catch (error) {
            console.error('Error submitting high score:', error);
        }
    };

    useEffect(() => {
        fetchHighScore();
    }, []);

    // Check collision
    const checkCollision = useCallback((piece, board, { dx = 0, dy = 0 } = {}) => {
        for (let y = 0; y < piece.shape.length; y++) {
            for (let x = 0; x < piece.shape[y].length; x++) {
                if (piece.shape[y][x]) {
                    const newX = piece.x + x + dx;
                    const newY = piece.y + y + dy;
                    
                    if (newX < 0 || newX >= BOARD_WIDTH || newY >= BOARD_HEIGHT) {
                        return true;
                    }
                    
                    if (newY >= 0 && board[newY][newX] !== 0) {
                        return true;
                    }
                }
            }
        }
        return false;
    }, []);

    // Rotate piece
    const rotatePiece = (piece) => {
        const rotated = piece.shape[0].map((_, index) =>
            piece.shape.map(row => row[index]).reverse()
        );
        return { ...piece, shape: rotated };
    };

    // Clear completed lines
    const clearLines = useCallback((board) => {
        const newBoard = board.filter(row => row.some(cell => cell === 0));
        const linesCleared = BOARD_HEIGHT - newBoard.length;
        const emptyRows = Array(linesCleared).fill().map(() => [...EMPTY_ROW]);
        
        return {
            board: [...emptyRows, ...newBoard],
            linesCleared
        };
    }, []);

    // Place piece on board
    const placePiece = useCallback((piece, board) => {
        const newBoard = board.map(row => [...row]);
        
        for (let y = 0; y < piece.shape.length; y++) {
            for (let x = 0; x < piece.shape[y].length; x++) {
                if (piece.shape[y][x]) {
                    const boardY = piece.y + y;
                    const boardX = piece.x + x;
                    if (boardY >= 0) {
                        newBoard[boardY][boardX] = piece.color;
                    }
                }
            }
        }
        
        return newBoard;
    }, []);

    // Move piece
    const movePiece = useCallback((dx, dy) => {
        if (!gameRunning || gameOver) return;
        
        if (!checkCollision(currentPiece, board, { dx, dy })) {
            setCurrentPiece(prev => ({
                ...prev,
                x: prev.x + dx,
                y: prev.y + dy
            }));
        } else if (dy > 0) {
            // Piece can't move down, place it
            const newBoard = placePiece(currentPiece, board);
            const { board: clearedBoard, linesCleared } = clearLines(newBoard);
            
            setBoard(clearedBoard);
            setLines(prev => prev + linesCleared);
            setScore(prev => {
                const lineScore = linesCleared * 100 * level;
                return prev + lineScore + 10; // 10 points for placing piece
            });
            
            // Check for game over
            if (currentPiece.y <= 0) {
                setGameOver(true);
                setGameRunning(false);
                if (score > highScore) {
                    submitHighScore(score);
                }
                onGameOver(score);
                return;
            }
            
            // Create new piece
            setCurrentPiece(nextPiece);
            setNextPiece(createPiece());
        }
    }, [gameRunning, gameOver, currentPiece, board, checkCollision, placePiece, clearLines, level, score, highScore, nextPiece, onGameOver, submitHighScore]);

    // Rotate current piece
    const rotatePieceHandler = useCallback(() => {
        if (!gameRunning || gameOver) return;
        
        const rotated = rotatePiece(currentPiece);
        if (!checkCollision(rotated, board)) {
            setCurrentPiece(rotated);
        }
    }, [gameRunning, gameOver, currentPiece, board, checkCollision]);

    // Drop piece automatically
    useEffect(() => {
        if (!gameRunning) return;
        
        const interval = setInterval(() => {
            movePiece(0, 1);
        }, dropTime);
        
        return () => clearInterval(interval);
    }, [gameRunning, dropTime, movePiece]);

    // Update level and drop speed
    useEffect(() => {
        const newLevel = Math.floor(lines / 3) + 1;  // Level up every 3 lines instead of 5
        setLevel(newLevel);
        // More aggressive speed increase: start at 1000ms, decrease by 80ms per level, minimum 50ms
        setDropTime(Math.max(50, 1000 - (newLevel - 1) * 80));
    }, [lines]);

    // Keyboard controls
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (!gameRunning || gameOver) return;
            
            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    movePiece(-1, 0);
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    movePiece(1, 0);
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    movePiece(0, 1);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    rotatePieceHandler();
                    break;
            }
        };
        
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [gameRunning, gameOver, movePiece, rotatePieceHandler]);

    // Create display board with current piece
    const displayBoard = () => {
        const display = board.map(row => [...row]);
        
        if (currentPiece && gameRunning && !gameOver) {
            for (let y = 0; y < currentPiece.shape.length; y++) {
                for (let x = 0; x < currentPiece.shape[y].length; x++) {
                    if (currentPiece.shape[y][x]) {
                        const boardY = currentPiece.y + y;
                        const boardX = currentPiece.x + x;
                        if (boardY >= 0 && boardY < BOARD_HEIGHT && boardX >= 0 && boardX < BOARD_WIDTH) {
                            display[boardY][boardX] = currentPiece.color;
                        }
                    }
                }
            }
        }
        
        return display;
    };

    const startGame = () => {
        setBoard(EMPTY_BOARD);
        setCurrentPiece(createPiece());
        setNextPiece(createPiece());
        setScore(0);
        setLevel(1);
        setLines(0);
        setGameRunning(true);
        setGameOver(false);
        setDropTime(1000);
    };

    const isDark = theme === 'dark';

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
            <div style={{
                background: isDark ? '#222' : '#fff',
                padding: '20px',
                borderRadius: '10px',
                color: isDark ? '#fff' : '#000',
                maxWidth: '800px',
                width: '90vw'
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px'
                }}>
                    <h2 style={{ margin: 0 }}>🎮 Easter Egg Tetris!</h2>
                    <button 
                        onClick={onClose}
                        style={{
                            background: '#ff4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '16px'
                        }}
                        title="Close game (or refresh page)"
                    >
                        ✖️
                    </button>
                </div>

                {/* Game Area */}
                <div style={{ display: 'flex', gap: '20px' }}>
                    {/* Game Board */}
                    <div style={{
                        border: '2px solid #333',
                        display: 'grid',
                        gridTemplateColumns: `repeat(${BOARD_WIDTH}, 25px)`,
                        gridTemplateRows: `repeat(${BOARD_HEIGHT}, 25px)`,
                        gap: '1px',
                        backgroundColor: '#111',
                        padding: '5px'
                    }}>
                        {displayBoard().flat().map((cell, index) => (
                            <div
                                key={index}
                                style={{
                                    width: '23px',
                                    height: '23px',
                                    backgroundColor: cell || '#222',
                                    border: cell ? '1px solid rgba(255,255,255,0.3)' : 'none'
                                }}
                            />
                        ))}
                    </div>

                    {/* Side Panel */}
                    <div style={{ minWidth: '200px' }}>
                        {/* Score */}
                        <div style={{ marginBottom: '20px' }}>
                            <h3>📊 Score: {score}</h3>
                            <p>🏆 High Score: {highScore}</p>
                            <p style={{ fontSize: '0.9em', color: '#666' }}>👑 Record Holder: {highScorePlayer}</p>
                            <p>📈 Level: {level}</p>
                            <p>📉 Lines: {lines}</p>
                        </div>

                        {/* Next Piece */}
                        <div style={{ marginBottom: '20px' }}>
                            <h4>⏭️ Next:</h4>
                            <div style={{
                                border: '1px solid #666',
                                padding: '10px',
                                backgroundColor: '#111',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 15px)',
                                gridTemplateRows: 'repeat(4, 15px)',
                                gap: '1px'
                            }}>
                                {Array(16).fill(0).map((_, index) => {
                                    const x = index % 4;
                                    const y = Math.floor(index / 4);
                                    const cell = nextPiece.shape[y]?.[x];
                                    return (
                                        <div
                                            key={index}
                                            style={{
                                                width: '13px',
                                                height: '13px',
                                                backgroundColor: cell ? nextPiece.color : '#222'
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        </div>

                        {/* Controls */}
                        <div style={{ marginBottom: '20px' }}>
                            <h4>🎮 Controls:</h4>
                            <p>⬅️ ➡️ Move</p>
                            <p>⬇️ Drop faster</p>
                            <p>⬆️ Rotate</p>
                        </div>

                        {/* Start/Game Over */}
                        {!gameRunning && !gameOver && (
                            <button
                                onClick={startGame}
                                style={{
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    width: '100%'
                                }}
                            >
                                🚀 Start Game
                            </button>
                        )}

                        {gameOver && (
                            <div>
                                <h3 style={{ color: '#ff4444' }}>💀 Game Over!</h3>
                                <p>Final Score: {score}</p>
                                <button
                                    onClick={startGame}
                                    style={{
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        width: '100%'
                                    }}
                                >
                                    🔄 Play Again
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TetrisGame;